import React from "react";
import { Box, Typography, Tooltip } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";

// Single source of truth for transaction-type -> icon, replacing the two hardcoded
// transactionList/transactionListDark arrays duplicated across Main.js and Defi.js. Kept
// deduplicated relative to the original arrays, which had unreachable duplicate entries
// (a later WITHDRAW/TRADE/SWAP entry can never be matched by .find() once an earlier one
// with the same transactionType already matched).
const TYPE_ICON_MAP = {
  REWARDS: { light: "rewards.svg", dark: "rewards.svg" },
  BUY: { light: "buy.svg", dark: "buy.svg" },
  SELL: { light: "sell.svg", dark: "sell.svg" },
  WITHDRAW: { light: "withdraw.svg", dark: "withdraw.svg" },
  TRANSACTION: { light: "transaction.svg", dark: "transaction.svg" },
  // Preserves the existing (slightly confusing but intentional) filename swap already in
  // production: "transferDark.svg" is the icon actually used in LIGHT mode, "transfer.svg" in
  // DARK mode - not changing that convention here, only extending it to the two new types.
  TRANSFER: { light: "transferDark.svg", dark: "transfer.svg" },
  SEND: { light: "send.svg", dark: "send.svg" },
  RECEIVE: { light: "send.svg", dark: "send.svg" },
  TRADE: { light: "trade.svg", dark: "trade.svg" },
  APPROVE: { light: "approve.svg", dark: "approve.svg" },
  DEPOSIT: { light: "deposit.svg", dark: "deposit.svg" },
  SWAP: { light: "approve.svg", dark: "approve.svg" },
  // New: direction-aware transfer labels (added alongside the generic TRANSFER type in the
  // backend's LocalTxnType enum). Reuse the same transfer glyph as TRANSFER - the direction
  // itself is carried by the colored arrow chip rendered alongside it in TransactionTypeBadge,
  // not by a distinct icon file, since no TRANSFER_IN/OUT-specific asset exists yet.
  TRANSFER_IN: { light: "transferDark.svg", dark: "transfer.svg" },
  TRANSFER_OUT: { light: "transferDark.svg", dark: "transfer.svg" },
};

const FALLBACK_ICON = "transaction.svg";

const iconUrl = (fileName) => `${process.env.REACT_APP_IMAGE_BASE_URL}/Transaction/${fileName}`;

/** Same lookup semantics as the old getTypeIcon: exact match, then normalized, then prefix, then substring. */
export const getTypeIconUrl = (rawType, isDarkTheme) => {
  const fallback = iconUrl(FALLBACK_ICON);
  if (!rawType) return fallback;

  const type = rawType.toString().toUpperCase().trim();
  const variant = isDarkTheme ? "dark" : "light";
  const entries = Object.entries(TYPE_ICON_MAP);

  const pick = (key) => iconUrl(TYPE_ICON_MAP[key][variant]);

  if (TYPE_ICON_MAP[type]) return pick(type);

  const normalized = type.replace(/_/g, "");
  let match = entries.find(([key]) => key.replace(/_/g, "") === normalized);
  if (match) return pick(match[0]);

  match = entries.find(([key]) => type.startsWith(key));
  if (match) return pick(match[0]);

  match = entries.find(([key]) => type.includes(key));
  if (match) return pick(match[0]);

  return fallback;
};

const DIRECTION_ARROW = {
  TRANSFER_IN: "↓", // ↓
  TRANSFER_OUT: "↑", // ↑
};

/**
 * Icon + label for a transaction type. For TRANSFER_IN/TRANSFER_OUT specifically, adds a small
 * colored directional arrow chip - deliberately more visible than a bare icon swap, since this
 * value changes whether the transaction is taxed and should be scannable at a glance in a list.
 *
 * isAiLabel marks that this label came from Gemini (txnTypeAi was set) rather than a structural/
 * deterministic source (the exchange's own data, an address match, a CEX's own transfer flag) -
 * there was previously no way to tell the two apart in this list at all.
 *
 * aiReason is Gemini's own plain-language explanation for this specific transaction's label
 * (backend's aiTxnReason) - shown in the "AI" chip's tooltip so the explanation is visibly
 * attributed as coming from AI rather than from the app's own logic.
 */
const TransactionTypeBadge = ({ type, label, size = 24, isAiLabel = false, aiReason = null }) => {
  const theme = useTheme();
  const isDarkTheme = theme.palette.type === "dark";
  const normalizedType = type ? type.toString().toUpperCase().trim() : "";
  const icon = getTypeIconUrl(type, isDarkTheme);
  const arrow = DIRECTION_ARROW[normalizedType];
  const arrowColor =
    normalizedType === "TRANSFER_IN"
      ? theme.palette.semantic.transferIn
      : normalizedType === "TRANSFER_OUT"
      ? theme.palette.semantic.transferOut
      : null;

  return (
    <Box display="flex" alignItems="center">
      <Box position="relative" display="inline-flex" mr={1}>
        <img
          src={icon}
          alt={label || type || "transaction type"}
          style={{ width: size, height: size }}
          onError={(e) => {
            e.target.src = iconUrl(FALLBACK_ICON);
          }}
        />
        {arrow && (
          <Box
            sx={{
              position: "absolute",
              bottom: -2,
              right: -4,
              width: 14,
              height: 14,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              lineHeight: 1,
              fontWeight: 700,
              color: "#fff",
              backgroundColor: arrowColor,
              border: `1.5px solid ${theme.palette.background.bg4box}`,
            }}
          >
            {arrow}
          </Box>
        )}
      </Box>
      {label !== null && (
        <Typography variant="body2" color="primary" style={{ fontWeight: 500 }}>
          {label}
        </Typography>
      )}
      {isAiLabel && (
        <Tooltip title={aiReason ? `AI: ${aiReason}` : "Labeled by AI"} arrow>
          <Box
            sx={{
              ml: 0.75,
              px: "6px",
              py: "1px",
              borderRadius: "4px",
              fontSize: 10,
              fontWeight: 700,
              lineHeight: "14px",
              color: "#fff",
              backgroundColor: "#7D56E7",
              cursor: "default",
            }}
          >
            AI
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default TransactionTypeBadge;
